import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  Plus,
  Search,
  Filter,
  Download,
  Send,
  Check,
  X,
  Clock,
  Euro,
  Building2,
  Calendar,
  ChevronRight,
  RefreshCw,
  Eye,
  Trash2,
  FileCheck,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Loader2,
  Upload,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ConnexionPlateforme } from '@/components/facturation/ConnexionPlateforme';
import { toast } from 'sonner';
import SuggestTVAButton from '@/components/SuggestTVAButton';
import { useWorkspace } from '@/contexts/WorkspaceContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const num = (v) => parseFloat(String(v ?? '').replace(',', '.')) || 0;
const saisieDecimale = (v) => v.replace(/[^0-9,.]/g, '');
const eur = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v || 0);

// Statuts avec couleurs
const STATUTS = {
  brouillon: { label: 'Brouillon', color: 'bg-gray-100 text-gray-800', icon: Clock },
  validee: { label: 'Validée', color: 'bg-blue-100 text-blue-800', icon: FileCheck },
  envoyee: { label: 'Envoyée', color: 'bg-yellow-100 text-yellow-800', icon: Send },
  deposee_ppf: { label: 'Déposée PPF', color: 'bg-purple-100 text-purple-800', icon: Upload },
  acceptee: { label: 'Acceptée', color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
  refusee: { label: 'Refusée', color: 'bg-red-100 text-red-800', icon: XCircle },
  payee: { label: 'Payée', color: 'bg-emerald-100 text-emerald-800', icon: Euro },
  annulee: { label: 'Annulée', color: 'bg-red-100 text-red-800', icon: X },
};

// Taux TVA courants
const TAUX_TVA = [
  { value: 20, label: '20% - Taux normal' },
  { value: 10, label: '10% - Taux intermédiaire' },
  { value: 5.5, label: '5.5% - Taux réduit' },
  { value: 2.1, label: '2.1% - Taux super-réduit' },
  { value: 0, label: '0% - Exonéré' },
];

export default function FacturationElectroniquePage() {
  const navigate = useNavigate();
  const [factures, setFactures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [statutFilter, setStatutFilter] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedFacture, setSelectedFacture] = useState(null);
  const [processing, setProcessing] = useState(false);

  // Formulaire nouvelle facture
  const [newFacture, setNewFacture] = useState({
    client_nom: '',
    client_siret: '',
    client_adresse: { ligne1: '', code_postal: '', ville: '', pays: 'FR' },
    client_email: '',
    emetteur_nom: '',
    emetteur_siret: '',
    emetteur_tva_intracommunautaire: '',
    emetteur_adresse: { ligne1: '', code_postal: '', ville: '', pays: 'FR' },
    lignes: [{ description: '', quantite: 1, prix_unitaire_ht: 0, taux_tva: 20 }],
    conditions_paiement: 'Paiement à 30 jours',
    date_echeance: '',
    notes: '',
  });

  const token = localStorage.getItem('token');
  const { activeSociete: currentSociete, loading: wsLoading } = useWorkspace();
  const societeId = currentSociete?.id;
  const auth = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    ...(societeId ? { 'X-Societe-Id': societeId } : {}),
  }), [token, societeId]);

  // Charger les factures
  const loadFactures = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statutFilter !== 'all') params.append('statut', statutFilter);
      
      const res = await fetch(`${API_URL}/api/facturation-electronique/factures?${params}`, {
        headers: auth(),
      });
      
      if (res.ok) {
        const data = await res.json();
        setFactures(data.factures || []);
        setStats(data.stats || {});
      }
    } catch (error) {
      toast.error('Erreur chargement factures');
    } finally {
      setLoading(false);
    }
  }, [auth, statutFilter]);

  useEffect(() => {
    if (!wsLoading) loadFactures();
  }, [loadFactures, wsLoading]);

  // Filtrer les factures
  const filteredFactures = factures.filter((f) => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        f.numero_facture?.toLowerCase().includes(search) ||
        f.client_nom?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  // Ajouter une ligne
  const addLigne = () => {
    setNewFacture({
      ...newFacture,
      lignes: [...newFacture.lignes, { description: '', quantite: 1, prix_unitaire_ht: 0, taux_tva: 20 }],
    });
  };

  // Supprimer une ligne
  const removeLigne = (index) => {
    if (newFacture.lignes.length > 1) {
      setNewFacture({
        ...newFacture,
        lignes: newFacture.lignes.filter((_, i) => i !== index),
      });
    }
  };

  // Mettre à jour une ligne
  const updateLigne = (index, field, value) => {
    const lignes = [...newFacture.lignes];
    lignes[index][field] = value;
    setNewFacture({ ...newFacture, lignes });
  };

  // Calculer les totaux
  const calculateTotals = (lignes) => {
    let totalHT = 0;
    let totalTVA = 0;
    
    lignes.forEach((l) => {
      const montantHT = num(l.quantite) * num(l.prix_unitaire_ht);
      totalHT += montantHT;
      totalTVA += montantHT * (l.taux_tva / 100);
    });
    
    return { totalHT: eur(totalHT), totalTVA: eur(totalTVA), totalTTC: eur(totalHT + totalTVA) };
  };

  // Créer une facture
  const handleCreateFacture = async () => {
    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/facturation-electronique/factures`, {
        method: 'POST',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newFacture,
          lignes: newFacture.lignes.map(l => ({ ...l, quantite: num(l.quantite), prix_unitaire_ht: num(l.prix_unitaire_ht) })),
        }),
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Erreur création');
      }
      
      const data = await res.json();
      toast.success(`Facture ${data.facture.numero_facture} créée en brouillon : validez-la puis cliquez sur « Envoyer par mail »`);
      setShowCreateDialog(false);
      resetForm();
      loadFactures();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessing(false);
    }
  };

  // Valider une facture
  const handleValider = async (factureId) => {
    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/facturation-electronique/factures/${factureId}/valider`, {
        method: 'POST',
        headers: auth(),
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Erreur validation');
      }
      
      toast.success('Facture validée - Conforme EN 16931');
      loadFactures();
      if (selectedFacture?.id === factureId) {
        loadFactureDetail(factureId);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessing(false);
    }
  };

  // Télécharger PDF
  const handleDownloadPDF = async (factureId, numeroFacture) => {
    try {
      const res = await fetch(`${API_URL}/api/facturation-electronique/factures/${factureId}/pdf`, {
        headers: auth(),
      });
      
      if (!res.ok) throw new Error('Erreur téléchargement');
      
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `facture_${numeroFacture}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF Factur-X téléchargé');
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Envoyer vers Chorus Pro
  const handleEnvoyerChorus = async (factureId) => {
    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/facturation-electronique/factures/${factureId}/envoyer-chorus`, {
        method: 'POST',
        headers: auth(),
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Erreur envoi');
      }
      
      toast.success('Facture envoyée vers Chorus Pro');
      loadFactures();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessing(false);
    }
  };

  // Envoyer au client par mail
  const handleEnvoyerEmail = async (facture) => {
    let email = facture.client_email;
    if (!email) {
      email = (window.prompt(`Email du client pour la facture ${facture.numero_facture} :`, '') || '').trim();
      if (!email) return;
      if (!email.includes('@')) { toast.error('Email invalide'); return; }
    } else if (!window.confirm(`Envoyer la facture ${facture.numero_facture} à ${email} ?`)) return;
    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/facturation-electronique/factures/${facture.id}/envoyer-email`, {
        method: 'POST', headers: { ...auth(), 'Content-Type': 'application/json' }, body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur envoi');
      toast.success(`Facture envoyée à ${data.email}`);
      loadFactures();
      if (selectedFacture?.id === facture.id) loadFactureDetail(facture.id);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessing(false);
    }
  };

  // Charger détail facture
  const loadFactureDetail = async (factureId) => {
    try {
      const res = await fetch(`${API_URL}/api/facturation-electronique/factures/${factureId}`, {
        headers: auth(),
      });
      
      if (res.ok) {
        const data = await res.json();
        setSelectedFacture(data.facture);
        setShowDetailDialog(true);
      }
    } catch (error) {
      toast.error('Erreur chargement détail');
    }
  };

  // Reset form
  const resetForm = () => {
    setNewFacture({
      client_nom: '',
      client_siret: '',
      client_adresse: { ligne1: '', code_postal: '', ville: '', pays: 'FR' },
      client_email: '',
      emetteur_nom: '',
      emetteur_siret: '',
      emetteur_tva_intracommunautaire: '',
      emetteur_adresse: { ligne1: '', code_postal: '', ville: '', pays: 'FR' },
      lignes: [{ description: '', quantite: 1, prix_unitaire_ht: 0, taux_tva: 20 }],
      conditions_paiement: 'Paiement à 30 jours',
      date_echeance: '',
      notes: '',
    });
  };

  const totals = calculateTotals(newFacture.lignes);

  return (
    <div className="p-6 space-y-6" data-testid="facturation-electronique-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="w-6 h-6 text-green-600" />
            Facturation Électronique
          </h1>
          <p className="text-muted-foreground">
            Factur-X conforme EN 16931 - Prêt pour 2026
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} className="bg-green-600 hover:bg-green-700 text-white" data-testid="fe-nouvelle-facture-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle facture
        </Button>
      </div>

      <ConnexionPlateforme />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{stats.total || 0}</div>
            <div className="text-sm text-muted-foreground">Total factures</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-gray-600">{stats.brouillons || 0}</div>
            <div className="text-sm text-muted-foreground">Brouillons</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-yellow-600">{stats.envoyees || 0}</div>
            <div className="text-sm text-muted-foreground">Envoyées</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-600">{stats.payees || 0}</div>
            <div className="text-sm text-muted-foreground">Payées</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-emerald-600">
              {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(stats.total_ttc || 0)}
            </div>
            <div className="text-sm text-muted-foreground">CA TTC</div>
          </CardContent>
        </Card>
      </div>

      {/* Filtres */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statutFilter} onValueChange={setStatutFilter}>
          <SelectTrigger className="w-[180px]">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Tous statuts" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous statuts</SelectItem>
            {Object.entries(STATUTS).map(([key, val]) => (
              <SelectItem key={key} value={key}>{val.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={loadFactures}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      {/* Table des factures */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>N° Facture</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Client</TableHead>
                <TableHead className="text-right">Montant TTC</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                  </TableCell>
                </TableRow>
              ) : filteredFactures.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    Aucune facture trouvée
                  </TableCell>
                </TableRow>
              ) : (
                filteredFactures.map((facture) => {
                  const statut = STATUTS[facture.statut] || STATUTS.brouillon;
                  const StatutIcon = statut.icon;
                  
                  return (
                    <TableRow key={facture.id} className="cursor-pointer hover:bg-muted/50">
                      <TableCell className="font-medium" onClick={() => loadFactureDetail(facture.id)}>
                        {facture.numero_facture}
                      </TableCell>
                      <TableCell onClick={() => loadFactureDetail(facture.id)}>
                        {new Date(facture.date_facture).toLocaleDateString('fr-FR')}
                      </TableCell>
                      <TableCell onClick={() => loadFactureDetail(facture.id)}>
                        <div>{facture.client_nom}</div>
                        <div className="text-xs text-muted-foreground">{facture.client_siret}</div>
                        {facture.facture_source_id && <div className="text-[11px] text-green-700" data-testid={`fe-lien-source-${facture.id}`}>Liée à la facture {facture.numero_facture} (Devis & Factures)</div>}
                        {facture.pa_statut && <div className="text-[11px] text-muted-foreground" data-testid={`fe-pa-statut-${facture.id}`}>{{ transmise_pa: 'Transmise à la plateforme', en_attente_pa: 'Partira dès la connexion plateforme', a_corriger: facture.pa_message, erreur_pa: `Erreur plateforme : ${facture.pa_message || ''}` }[facture.pa_statut]}</div>}
                      </TableCell>
                      <TableCell className="text-right font-medium" onClick={() => loadFactureDetail(facture.id)}>
                        {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(facture.total_ttc)}
                      </TableCell>
                      <TableCell onClick={() => loadFactureDetail(facture.id)}>
                        <Badge className={statut.color}>
                          <StatutIcon className="w-3 h-3 mr-1" />
                          {statut.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {facture.statut === 'brouillon' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleValider(facture.id)}
                              disabled={processing}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                          )}
                          {facture.statut === 'validee' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEnvoyerChorus(facture.id)}
                              disabled={processing}
                              title="Envoyer vers Chorus Pro"
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                          )}
                          {facture.statut !== 'brouillon' && (
                            <Button size="sm" variant="outline" onClick={() => handleEnvoyerEmail(facture)} disabled={processing}
                              title="Envoyer au client par mail" data-testid={`fe-envoyer-mail-${facture.id}`}>
                              <Send className="w-4 h-4 text-green-700" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDownloadPDF(facture.id, facture.numero_facture)}
                            title="Télécharger PDF Factur-X"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog Création Facture */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white text-stone-900" data-testid="fe-create-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-green-600" />
              Nouvelle Facture Électronique
            </DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="client" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="client">Client</TabsTrigger>
              <TabsTrigger value="emetteur">Émetteur</TabsTrigger>
              <TabsTrigger value="lignes">Lignes</TabsTrigger>
              <TabsTrigger value="options">Options</TabsTrigger>
            </TabsList>

            <TabsContent value="client" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nom / Raison sociale *</Label>
                  <Input
                    value={newFacture.client_nom}
                    onChange={(e) => setNewFacture({ ...newFacture, client_nom: e.target.value })}
                    placeholder="Nom du client"
                  />
                </div>
                <div className="space-y-2">
                  <Label>SIRET (si professionnel)</Label>
                  <Input
                    value={newFacture.client_siret}
                    onChange={(e) => setNewFacture({ ...newFacture, client_siret: e.target.value })}
                    placeholder="14 chiffres"
                    maxLength={14}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Adresse *</Label>
                <Input
                  value={newFacture.client_adresse.ligne1}
                  onChange={(e) => setNewFacture({
                    ...newFacture,
                    client_adresse: { ...newFacture.client_adresse, ligne1: e.target.value }
                  })}
                  placeholder="Adresse"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Code postal *</Label>
                  <Input
                    value={newFacture.client_adresse.code_postal}
                    onChange={(e) => setNewFacture({
                      ...newFacture,
                      client_adresse: { ...newFacture.client_adresse, code_postal: e.target.value }
                    })}
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>Ville *</Label>
                  <Input
                    value={newFacture.client_adresse.ville}
                    onChange={(e) => setNewFacture({
                      ...newFacture,
                      client_adresse: { ...newFacture.client_adresse, ville: e.target.value }
                    })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={newFacture.client_email}
                  onChange={(e) => setNewFacture({ ...newFacture, client_email: e.target.value })}
                  placeholder="email@client.fr"
                />
              </div>
            </TabsContent>

            <TabsContent value="emetteur" className="space-y-4 mt-4">
              <p className="text-sm text-muted-foreground">
                Si laissé vide, les informations de votre société seront utilisées automatiquement.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nom / Raison sociale</Label>
                  <Input
                    value={newFacture.emetteur_nom}
                    onChange={(e) => setNewFacture({ ...newFacture, emetteur_nom: e.target.value })}
                    placeholder="Votre société"
                  />
                </div>
                <div className="space-y-2">
                  <Label>SIRET</Label>
                  <Input
                    value={newFacture.emetteur_siret}
                    onChange={(e) => setNewFacture({ ...newFacture, emetteur_siret: e.target.value })}
                    placeholder="14 chiffres"
                    maxLength={14}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>N° TVA Intracommunautaire</Label>
                <Input
                  value={newFacture.emetteur_tva_intracommunautaire}
                  onChange={(e) => setNewFacture({ ...newFacture, emetteur_tva_intracommunautaire: e.target.value })}
                  placeholder="FR12345678901"
                />
              </div>
              <div className="space-y-2">
                <Label>Adresse</Label>
                <Input
                  value={newFacture.emetteur_adresse.ligne1}
                  onChange={(e) => setNewFacture({
                    ...newFacture,
                    emetteur_adresse: { ...newFacture.emetteur_adresse, ligne1: e.target.value }
                  })}
                  placeholder="Adresse"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Code postal</Label>
                  <Input
                    value={newFacture.emetteur_adresse.code_postal}
                    onChange={(e) => setNewFacture({
                      ...newFacture,
                      emetteur_adresse: { ...newFacture.emetteur_adresse, code_postal: e.target.value }
                    })}
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>Ville</Label>
                  <Input
                    value={newFacture.emetteur_adresse.ville}
                    onChange={(e) => setNewFacture({
                      ...newFacture,
                      emetteur_adresse: { ...newFacture.emetteur_adresse, ville: e.target.value }
                    })}
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="lignes" className="space-y-4 mt-4">
              {newFacture.lignes.map((ligne, index) => (
                <div key={index} className="p-4 border rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Ligne {index + 1}</span>
                    {newFacture.lignes.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeLigne(index)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Description *</Label>
                    <Input
                      value={ligne.description}
                      onChange={(e) => updateLigne(index, 'description', e.target.value)}
                      placeholder="Description du produit ou service"
                    />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="space-y-2">
                      <Label>Quantité</Label>
                      <Input
                        type="text"
                        inputMode="decimal"
                        value={ligne.quantite}
                        onChange={(e) => updateLigne(index, 'quantite', saisieDecimale(e.target.value))}
                        data-testid={`fe-ligne-qte-${index}`}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Prix unitaire HT</Label>
                      <Input
                        type="text"
                        inputMode="decimal"
                        value={ligne.prix_unitaire_ht}
                        onChange={(e) => updateLigne(index, 'prix_unitaire_ht', saisieDecimale(e.target.value))}
                        data-testid={`fe-ligne-pu-${index}`}
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label>TVA</Label>
                      <div className="flex gap-1">
                        <Select
                          value={String(ligne.taux_tva)}
                          onValueChange={(v) => updateLigne(index, 'taux_tva', parseFloat(v))}
                        >
                          <SelectTrigger className="flex-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {TAUX_TVA.map((t) => (
                              <SelectItem key={t.value} value={String(t.value)}>{t.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <SuggestTVAButton
                          description={ligne.description || ''}
                          contexteActivite="agriculture"
                          montantHt={num(ligne.quantite) * num(ligne.prix_unitaire_ht)}
                          onApply={(taux) => updateLigne(index, 'taux_tva', parseFloat(taux))}
                          size="icon"
                          variant="outline"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Total HT</Label>
                      <div className="h-10 flex items-center font-medium">
                        {eur(num(ligne.quantite) * num(ligne.prix_unitaire_ht))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              <Button variant="outline" onClick={addLigne} className="w-full">
                <Plus className="w-4 h-4 mr-2" />
                Ajouter une ligne
              </Button>

              <div className="p-4 bg-muted rounded-lg">
                <div className="flex justify-between text-sm">
                  <span>Total HT</span>
                  <span>{totals.totalHT}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span>Total TVA</span>
                  <span>{totals.totalTVA}</span>
                </div>
                <div className="flex justify-between font-bold mt-2 pt-2 border-t">
                  <span>Total TTC</span>
                  <span>{totals.totalTTC}</span>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="options" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date d'échéance</Label>
                  <Input
                    type="date"
                    value={newFacture.date_echeance}
                    onChange={(e) => setNewFacture({ ...newFacture, date_echeance: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Conditions de paiement</Label>
                  <Input
                    value={newFacture.conditions_paiement}
                    onChange={(e) => setNewFacture({ ...newFacture, conditions_paiement: e.target.value })}
                    placeholder="Paiement à 30 jours"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notes / Mentions légales</Label>
                <Textarea
                  value={newFacture.notes}
                  onChange={(e) => setNewFacture({ ...newFacture, notes: e.target.value })}
                  placeholder="Notes additionnelles..."
                  rows={3}
                />
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleCreateFacture}
              data-testid="fe-creer-facture-btn"
              disabled={processing || !newFacture.client_nom || newFacture.lignes.every(l => !l.description)}
              className="bg-green-600 hover:bg-green-700"
            >
              {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Créer la facture
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Détail Facture */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-3xl bg-white text-stone-900" data-testid="fe-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-green-600" />
              Facture {selectedFacture?.numero_facture}
            </DialogTitle>
          </DialogHeader>

          {selectedFacture && (
            <div className="space-y-4">
              {/* Statut et validation */}
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div className="flex items-center gap-2">
                  <Badge className={STATUTS[selectedFacture.statut]?.color}>
                    {STATUTS[selectedFacture.statut]?.label}
                  </Badge>
                  {selectedFacture.validation?.valid && (
                    <Badge className="bg-green-100 text-green-800">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Conforme EN 16931
                    </Badge>
                  )}
                </div>
                <div className="text-sm text-muted-foreground">
                  Créée le {new Date(selectedFacture.created_at).toLocaleDateString('fr-FR')}
                </div>
              </div>

              {/* Infos émetteur / client */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium mb-2">Émetteur</h4>
                  <div className="text-sm space-y-1">
                    <div className="font-medium">{selectedFacture.emetteur?.nom}</div>
                    <div>SIRET: {selectedFacture.emetteur?.siret}</div>
                    <div>{selectedFacture.emetteur?.adresse?.ligne1}</div>
                    <div>{selectedFacture.emetteur?.adresse?.code_postal} {selectedFacture.emetteur?.adresse?.ville}</div>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium mb-2">Client</h4>
                  <div className="text-sm space-y-1">
                    <div className="font-medium">{selectedFacture.client_nom}</div>
                    <div>SIRET: {selectedFacture.client_siret}</div>
                    <div>{selectedFacture.client_adresse?.ligne1}</div>
                    <div>{selectedFacture.client_adresse?.code_postal} {selectedFacture.client_adresse?.ville}</div>
                  </div>
                </div>
              </div>

              {/* Lignes */}
              <div>
                <h4 className="font-medium mb-2">Lignes de facturation</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Qté</TableHead>
                      <TableHead className="text-right">P.U. HT</TableHead>
                      <TableHead className="text-right">TVA</TableHead>
                      <TableHead className="text-right">Total HT</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedFacture.lignes?.map((ligne, i) => (
                      <TableRow key={i}>
                        <TableCell>{ligne.description}</TableCell>
                        <TableCell className="text-right">{ligne.quantite}</TableCell>
                        <TableCell className="text-right">{eur(ligne.prix_unitaire_ht)}</TableCell>
                        <TableCell className="text-right">{ligne.taux_tva}%</TableCell>
                        <TableCell className="text-right font-medium">{eur(ligne.montant_ht)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Totaux */}
              <div className="flex justify-end">
                <div className="w-64 p-3 bg-muted rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span>Total HT</span>
                    <span>{eur(selectedFacture.total_ht)}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span>Total TVA</span>
                    <span>{eur(selectedFacture.total_tva)}</span>
                  </div>
                  <div className="flex justify-between font-bold mt-2 pt-2 border-t">
                    <span>Total TTC</span>
                    <span>{eur(selectedFacture.total_ttc)}</span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4 border-t">
                {selectedFacture.statut === 'brouillon' && (
                  <Button onClick={() => handleValider(selectedFacture.id)} disabled={processing}>
                    <Check className="w-4 h-4 mr-2" />
                    Valider
                  </Button>
                )}
                {selectedFacture.statut === 'validee' && (
                  <Button onClick={() => handleEnvoyerChorus(selectedFacture.id)} disabled={processing}>
                    <Send className="w-4 h-4 mr-2" />
                    Envoyer Chorus Pro
                  </Button>
                )}
                {selectedFacture.statut !== 'brouillon' && (
                  <Button onClick={() => handleEnvoyerEmail(selectedFacture)} disabled={processing}
                    className="bg-green-600 hover:bg-green-700 text-white" data-testid="fe-detail-envoyer-mail">
                    <Send className="w-4 h-4 mr-2" />
                    Envoyer par mail
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => handleDownloadPDF(selectedFacture.id, selectedFacture.numero_facture)}
                >
                  <Download className="w-4 h-4 mr-2" />
                  PDF Factur-X
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
